import { Injectable } from '@angular/core';
import { MatSnackBarRef } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { findIndex, isEqual, isMatch } from 'lodash-es';
import { ReplaySubject, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  skipUntil,
  tap,
  throttleTime,
} from 'rxjs/operators';
import { io } from 'socket.io-client';

import {
  LiveUpdatesApi,
  LiveUpdatesCreateSocketResponse,
  LiveUpdatesSubscriptionQuery,
  SmartNotesWebsocketResponse,
} from '@vi-iot/client';
import { ViToastComponent, ViToastService } from '@vi-iot/ui';

import { AuthStore } from '@store';
import {
  APP_INSIGHTS_LIVE_UPDATES,
  AppInsightsMonitoringService,
  LogLevelEnum,
} from '../../../app/shared/services/app-insights';

export interface Link {
  _href: string;
  _rel: ['topic'];
}

export enum DisconnectReason {
  IoServerDisconnect = 'io server disconnect',
  IoClientDisconnect = 'io client disconnect',
  PingTimeout = 'ping timeout',
  TransportatClose = 'transport close',
  TransportatError = 'transport error',
}

@Injectable({
  providedIn: 'root',
})
export class SocketIoService {
  readonly liveUpdatesSubscriptionSessionKey = 'liveUpdatesSubscription';
  onWebsocketError$ = new Subject();

  websocketFeatureData$ = new ReplaySubject();
  websocketAggregatedStatusData$ = new ReplaySubject();
  websocketSmartNotesData$ = new ReplaySubject();
  updateQuery$ = new Subject<LiveUpdatesSubscriptionQuery[]>();

  cachedQueries: LiveUpdatesSubscriptionQuery[] = [];
  socket: any;
  socketId: string;

  private toastRef: MatSnackBarRef<ViToastComponent>;
  private readonly offlineToastKeys = {
    offlineMode: {
      message: 'GENERAL.OFFLINE_MODE.MESSAGE',
    },
    onlineMode: {
      message: 'GENERAL.ONLINE_MODE.MESSAGE',
    },
  };

  constructor(
    public translate: TranslateService,
    public viToastService: ViToastService,
    public authStore: AuthStore,
    private liveUpdatesApi: LiveUpdatesApi,
    private appInsights: AppInsightsMonitoringService
  ) {
    window.addEventListener('online', this.networkingOnline.bind(this));
    window.addEventListener('offline', this.networkingOffline.bind(this));

    this.onWebsocketError$.pipe(throttleTime(3000)).subscribe((error: { statusCode: number }) => {
      this.onWebsocketError(error);
    });

    this.updateQuery$
      .pipe(
        skipUntil(this.authStore.authenticated$),
        filter(() => !this.authStore.isDemoSync),
        distinctUntilChanged(isEqual),
        tap((query) => {
          const notAddedQueries = query.filter(
            // find queries not added to cachedQuery
            (entry) => !this.cachedQueries.some((cachedQuery) => isMatch(cachedQuery, entry))
          );
          if (notAddedQueries.length) {
            this.cachedQueries = this.cachedQueries.concat(query);
          }
        }),
        debounceTime(1000)
      )
      .subscribe(() => {
        this.appInsights.logEvent(APP_INSIGHTS_LIVE_UPDATES.ON_UPDATE_QUERY, LogLevelEnum.DEBUG, {
          cachedQueries: this.cachedQueries,
        });
        this.connectToSocket();
      });
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket.close();
    }
    this.socketId = null;
    this.cachedQueries = [];
    this.updateQuery$.next([]);
  }

  removeSingleQuery(query: LiveUpdatesSubscriptionQuery[]): void {
    const foundQueryIndex = findIndex(this.cachedQueries, query[0]);

    if (foundQueryIndex !== -1) {
      this.cachedQueries.splice(foundQueryIndex, 1);
    }
  }

  connectToSocket(): void {
    const cachedQueries =
      this.cachedQueries.length > 0
        ? this.cachedQueries
        : JSON.parse(sessionStorage.getItem(this.liveUpdatesSubscriptionSessionKey));

    if (!cachedQueries || cachedQueries.length === 0) {
      return;
    }

    this.liveUpdatesApi.createSubscription(cachedQueries).subscribe({
      next: (response) => {
        this.appInsights.logEvent(
          APP_INSIGHTS_LIVE_UPDATES.INVOKE_CALL_CREATE_SUBSCRIPTION_ATTEMPT,
          LogLevelEnum.DEBUG,
          { cachedQueries: this.cachedQueries }
        );

        this.estabilishWebsocketConnection(response);
      },
      error: () => {
        this.appInsights.logEvent(
          APP_INSIGHTS_LIVE_UPDATES.INVOKE_CALL_CREATE_SUBSCRIPTION_FAILED,
          LogLevelEnum.DEBUG,
          { cachedQueries: this.cachedQueries }
        );

        console.log('call to liveUpdatesApi.createSubscription has failed');
      },
      complete: () => {
        this.appInsights.logEvent(
          APP_INSIGHTS_LIVE_UPDATES.INVOKE_CALL_CREATE_SUBSCRIPTION_SUCCESS,
          LogLevelEnum.DEBUG,
          { cachedQueries: this.cachedQueries }
        );
        sessionStorage.setItem(
          this.liveUpdatesSubscriptionSessionKey,
          JSON.stringify(this.cachedQueries)
        );
      },
    });
  }

  private estabilishWebsocketConnection(response: LiveUpdatesCreateSocketResponse): void {
    this.socketId = response?.socketId;

    this.socket = io(response.url, {
      path: response.path,
      transports: ['websocket'],
      reconnection: false,
    });

    this.socket.on('connect', () => {
      console.log('LiveUpdate connected');
    });

    this.socket.on('error', (error) => {
      this.onWebsocketError$.next(error);
    });

    this.socket.on('disconnect', (reason: DisconnectReason) => {
      console.log(`LiveUpdate disconnected: ${reason}`);
    });

    this.socket.on('smart-note', (smartNote: SmartNotesWebsocketResponse) => {
      this.websocketSmartNotesData$.next(smartNote);
    });

    this.socket.on('feature', (e) => {
      this.websocketFeatureData$.next(e);
    });

    this.socket.on('gateway-aggregated-status-changed', (e) => {
      this.websocketAggregatedStatusData$.next(e);
    });
  }

  reconnect(): void {
    if (this.socketId) {
      return;
    } else {
      if (navigator.onLine) {
        this.appInsights.logEvent(APP_INSIGHTS_LIVE_UPDATES.RECONNECT, LogLevelEnum.DEBUG);
        this.connectToSocket();
      }
    }
  }

  private onWebsocketError(error: { statusCode: number }): void {
    this.appInsights.logEvent(APP_INSIGHTS_LIVE_UPDATES.ON_ERROR, LogLevelEnum.DEBUG, { error });

    if (error && error.statusCode === 401) {
      console.log('LiveUpdate onError 401 - session expired');
      this.authStore.fetchCSRFTokenOn401Subject$.next();
    } else if (error && navigator.onLine) {
      console.log('LiveUpdate onError - web sockets will now try to reconnect');
      console.log(error);
      this.disconnect();
      this.reconnect();
    }
  }

  private networkingOnline(): void {
    this.appInsights.logEvent(APP_INSIGHTS_LIVE_UPDATES.ON_NETWORK_ONLINE, LogLevelEnum.DEBUG);

    this.dismissToast();
    this.translate
      .get([this.offlineToastKeys.onlineMode.message])
      .subscribe((translations: { [key: string]: string }) => {
        const data = {
          message: translations[this.offlineToastKeys.onlineMode.message],
        };
        this.viToastService.info(data);
      });

    this.reconnect();
  }

  private networkingOffline(): void {
    this.dismissToast();
    this.translate
      .get([this.offlineToastKeys.offlineMode.message])
      .subscribe((translations: { [key: string]: string }) => {
        const data = {
          message: translations[this.offlineToastKeys.offlineMode.message],
        };
        this.toastRef = this.viToastService.warningRef(data, { duration: 0 });
      });

    this.disconnect();
  }

  private dismissToast(): void {
    if (this.toastRef) {
      this.toastRef.dismiss();
    }
  }
}
